import { $t } from '@/locales';
import { hexToRgb } from './colorUtils';

/**
 * 将对象转换为option列表
 *
 * @example
 *   ```ts
 *   const record = {
 *     key1: 'label1',
 *     key2: 'label2'
 *   };
 *   const options = transformRecordToOption(record);
 *   // [
 *   //   { value: 'key1', label: 'label1' },
 *   //   { value: 'key2', label: 'label2' }
 *   // ]
 *   ```;
 *
 * @param record
 */
export function transformRecordToOption<T extends Record<string, string>>(record: T) {
  return Object.entries(record).map(([value, label]) => ({
    value,
    label
  })) as CommonType.Option<keyof T>[];
}

/**
 * Translate options
 *
 * @param options
 */
export function translateOptions(options: CommonType.Option<string>[]) {
  return options.map(option => ({
    ...option,
    label: $t(option.label as App.I18n.I18nKey)
  }));
}

/**
 * 切换html的class
 *
 * @param className
 */
export function toggleHtmlClass(className: string) {
  function add() {
    document.documentElement.classList.add(className);
  }

  function remove() {
    document.documentElement.classList.remove(className);
  }

  return {
    add,
    remove
  };
}

export const isDev = () => {
  return Boolean(import.meta.env.DEV);
};

/**
 * 将 limit-offset 模式转换为 page-pageSize 模式
 *
 * @param limit 每页条数
 * @param offset 偏移量
 * @returns 返回 page 和 pageSize
 */
export function convertOffsetToPage(limit: number, offset: number): { page: number; pageSize: number } {
  const pageSize = limit;
  const page = Math.floor(offset / limit) + 1;

  return { page, pageSize };
}

/**
 * 将 page-pageSize 模式转换为 limit-offset 模式
 *
 * @param page 页码
 * @param pageSize 每页条数
 * @returns 返回 limit 和 offset
 */
export function convertPageToOffset(page: number, pageSize: number): { limit: number; offset: number } {
  const limit = pageSize;
  const offset = (page - 1) * pageSize;

  return { limit, offset };
}

/**
 * 显示平台信息
 *
 * @param platform
 * @returns
 */
export function displayPlatformInfo(outPlatform: string) {
  let platform = outPlatform;
  if (platform === 'wechat') {
    platform = 'mp-weixin';
  } else if (platform === 'alipay') {
    platform = 'mp-alipay';
  }
  if (platform === 'mp-weixin') {
    return {
      label: '微信',
      payIcon: 'mingcute:wechat-pay-line',
      icon: 'mingcute:wechat-line',
      type: 'mp-weixin',
      color: '#1AAD19',
      bgColor: hexToRgb('#1AAD19', 0.1)
    };
  } else if (platform === 'mp-alipay') {
    return {
      label: '支付宝',
      type: 'mp-alipay',
      payIcon: 'mingcute:alipay-line',
      icon: 'mingcute:alipay-line',
      color: '#1890FF',
      bgColor: hexToRgb('#1890FF', 0.1)
    };
  } else if (platform === 'platform') {
    return {
      label: '余额',
      type: 'platform',
      payIcon: 'mingcute:wallet-line',
      icon: 'i-mingcute-wallet-line',
      color: '#578af3',
      bgColor: hexToRgb('#578af3', 0.1)
    };
  } else if (platform === 'card') {
    return {
      label: '水卡',
      type: 'card',
      payIcon: 'mingcute:bank-card-line',
      icon: 'mingcute:bank-card-line',
      color: '#00B4D8',
      bgColor: hexToRgb('#00B4D8', 0.1)
    };
  }
  return {
    label: '其他',
    payIcon: 'mingcute:cash-2-line',
    icon: 'mingcute:android-line',
    type: 'other',
    color: '#999',
    bgColor: hexToRgb('#999', 0.1)
  };
}
